import type { CSSProperties } from 'react';
import {
  AttachmentIcon,
  Button,
  Card,
  CardContent,
  Chip,
  CollapseIcon,
  ExpandIcon,
  MessageHeader,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@all-manager/design-system-v2';
import type { InboxAttachmentRow, InboxEmail } from '../../types/inbox.types';
import DocumentSummaryCard from '../DocumentSummaryCard/DocumentSummaryCard';
import { type EmailViewMode, useEmailContentView } from './useEmailContentView';

type Props = {
  email: InboxEmail;
  activeViewMode: EmailViewMode;
  hasHtml: boolean;
  cleanedBodyText: string;
  relevantAttachments: InboxAttachmentRow[];
  signatureAttachments: InboxAttachmentRow[];
  showSignatureAttachments: boolean;
  onToggleViewMode: (mode: EmailViewMode) => void;
  onToggleSignatureAttachments: () => void;
  onDownloadAttachment: (att: InboxAttachmentRow) => void;
};

// Native elements only: the DS components are closed to style overrides.
const IFRAME_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '60vh',
  minHeight: 360,
  border: 'none',
  borderRadius: 4,
};

const TEXT_BODY_STYLE: CSSProperties = {
  maxWidth: '75ch',
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
};

export default function EmailContentView(props: Props) {
  const hook = useEmailContentView(props);

  return (
    <Stack spacing={3}>
      <DocumentSummaryCard summary={props.email.document_summary} email={props.email} />

      <MessageHeader
        sender={hook.sender}
        address={props.email.from_email}
        date={hook.receivedLabel}
        dateTime={props.email.received_at}
        action={props.hasHtml ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={props.activeViewMode}
            onChange={hook.handleViewModeChange}
            aria-label="Format d'affichage du message"
          >
            <ToggleButton value="html">HTML</ToggleButton>
            <ToggleButton value="text">Texte</ToggleButton>
          </ToggleButtonGroup>
        ) : undefined}
      />

      <Stack spacing={1}>
        <Typography variant="h6">Message</Typography>
        {props.activeViewMode === 'html' && props.email.body_html ? (
          <Card>
            <iframe
              srcDoc={props.email.body_html}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              title="Message HTML"
              style={IFRAME_STYLE}
            />
          </Card>
        ) : (
          <Card>
            <CardContent>
              {props.cleanedBodyText ? (
                <div style={TEXT_BODY_STYLE}>
                  <Typography variant="body1">{props.cleanedBodyText}</Typography>
                </div>
              ) : (
                <Typography variant="body2">Aucun contenu texte dans cet e-mail.</Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      {hook.hasAttachments && (
        <Stack spacing={1}>
          <Typography variant="h6">Pièces jointes ({props.relevantAttachments.length})</Typography>
          <Stack direction="row" spacing={1} wrap="wrap">
            {props.relevantAttachments.map((att) => (
              <Chip
                key={att.id}
                icon={<AttachmentIcon />}
                label={att.filename}
                variant="outlined"
                onClick={function handleDownload() {
                  props.onDownloadAttachment(att);
                }}
              />
            ))}
          </Stack>

          {props.signatureAttachments.length > 0 && (
            <Stack spacing={1} alignItems="flex-start">
              <Button
                variant="text"
                size="small"
                color="default"
                startIcon={props.showSignatureAttachments ? <CollapseIcon /> : <ExpandIcon />}
                aria-expanded={props.showSignatureAttachments}
                onClick={props.onToggleSignatureAttachments}
              >
                {props.showSignatureAttachments
                  ? 'Masquer les images de signature'
                  : `Afficher les images de signature (${props.signatureAttachments.length})`}
              </Button>
              {props.showSignatureAttachments && (
                <Stack direction="row" spacing={1} wrap="wrap">
                  {props.signatureAttachments.map((att) => (
                    <Chip
                      key={att.id}
                      icon={<AttachmentIcon />}
                      label={att.filename}
                      size="small"
                      variant="outlined"
                      onClick={function handleDownloadSignature() {
                        props.onDownloadAttachment(att);
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
